import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import type { Deal, Pipeline } from '../deals/types';

/**
 * The deals behind a lead or a company, as rows somebody can open.
 *
 * ── The gap this closes ────────────────────────────────────────────────────
 *
 * Kyu, checking the model against HubSpot: "a company profile will have all
 * the leads... the lead profile will have the company... and whatever deal is
 * associated to both. They're both interconnected. Correct?" The DATA was —
 * every link below already existed as a column — but the screens showed a
 * rollup badge on the lead, nothing at all on the company, and no way to
 * click from either to the deal itself.
 *
 * These are the reads for the missing two-thirds. The third piece, jumping
 * INTO a deal, rides on the /deals?deal=<id> address the Inbox already uses —
 * plus ?pipeline=, added in this drop, because the board opens on CG Deals
 * and a partnerships deal id would otherwise be looked up in the wrong list.
 *
 * ── What these deliberately do not do ──────────────────────────────────────
 *
 * No invented links. A deal with no lead_id shows on no lead; 41 of 53 deals
 * had no company_id the day this shipped and they show on no company. That
 * emptiness is the true state and the team was asked (Slack, 28 Aug) to start
 * linking — the screens must show the gap, not paper over it.
 *
 * Access follows RLS exactly as lead_deal_outcomes does: the deals module
 * gate decides what the caller can see, and a person without Deals access
 * gets an empty list rather than this view becoming a way around the gate.
 */

/** One deal, as the lead and company panels list it. */
export interface DealLink {
  id: string;
  pipeline: Pipeline;
  name: string;
  outcome: Deal['outcome'];
  closed_on: string | null;
  created_at: string;
  /** The stage's name, or null when the deal has no stage. */
  stage_name: string | null;
}

/**
 * The order a deal list reads in, on a lead and on a company.
 *
 * Kyu, 9 September, on HubSpot's company page: "you can see the deal or
 * deals if there have been many deals in the past. Active deals on top,
 * most recent." So: open deals first, newest first; then closed deals,
 * most recently closed first. A closed deal with no closing date (older
 * rows) falls back to when it was made, so it still has a place.
 */
export function sortDealLinks(deals: readonly DealLink[]): DealLink[] {
  const stamp = (deal: DealLink) =>
    deal.outcome === 'open' ? deal.created_at : (deal.closed_on ?? deal.created_at);
  return [...deals].sort((a, b) => {
    const aOpen = a.outcome === 'open' ? 0 : 1;
    const bOpen = b.outcome === 'open' ? 0 : 1;
    if (aOpen !== bOpen) return aOpen - bOpen;
    const ta = stamp(a);
    const tb = stamp(b);
    return ta === tb ? 0 : ta < tb ? 1 : -1;
  });
}

/**
 * Keyed under the deals ROOT on purpose: every mutation in the deals module
 * invalidates ['deals'], so a rename or an outcome change on the board
 * refreshes these lists without either side knowing about the other.
 */
export const dealLinkKeys = {
  all: () => ['deals', 'linked'] as const,
  forLead: (leadId: string) => ['deals', 'linked', 'lead', leadId] as const,
  forCompany: (companyId: string) => ['deals', 'linked', 'company', companyId] as const,
};

/**
 * The full column list, including the joined stage name. The house lesson
 * applies: every column the DealLink type names must appear here, or rows
 * arrive with silent undefineds (see leadActivity.ts).
 */
const LINK_COLUMNS = 'id, pipeline, name, outcome, closed_on, created_at, stage:deal_stages(name)';

interface RawLink {
  id: string;
  pipeline: Pipeline;
  name: string;
  outcome: Deal['outcome'];
  closed_on: string | null;
  created_at: string;
  stage: { name: string } | null;
}

function toLink(row: RawLink): DealLink {
  return {
    id: row.id,
    pipeline: row.pipeline,
    name: row.name,
    outcome: row.outcome,
    closed_on: row.closed_on,
    created_at: row.created_at,
    stage_name: row.stage === null ? null : String(row.stage.name),
  };
}

async function fetchLinks(
  client: SupabaseClient,
  column: 'lead_id' | 'company_id',
  id: string,
): Promise<DealLink[]> {
  const { data, error } = await client
    .from('deals')
    .select(LINK_COLUMNS)
    .eq(column, id)
    .order('created_at', { ascending: false });
  if (error) {
    throw new Error(error.message);
  }
  return sortDealLinks(((data ?? []) as unknown as RawLink[]).map(toLink));
}

export function useDealsForLead(leadId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: dealLinkKeys.forLead(leadId),
    queryFn: () => fetchLinks(client, 'lead_id', leadId),
    retry: false,
  });
}

export function useDealsForCompany(companyId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: dealLinkKeys.forCompany(companyId),
    queryFn: () => fetchLinks(client, 'company_id', companyId),
    retry: false,
  });
}

/**
 * Where clicking a deal row goes.
 *
 * The pipeline travels in the address because the board's state starts on CG
 * Deals: without it, a link to a partnerships deal would open the wrong
 * board, find nothing, and silently show no drawer — a dead link that looks
 * like a working page.
 */
export function dealJumpHref(deal: Pick<DealLink, 'id' | 'pipeline'>): string {
  return `/deals?pipeline=${deal.pipeline}&deal=${deal.id}`;
}
